<template>
  <div class="grid-y flex-grow">
    <Loading :loading="loading">
      <div class="cell p-8 communication-settings settings-sms-mobile">
        <div class="settings-header">
          <div>
            <b class="text-16px leading-18px title-divided">Коммуникации / SMS</b>
            <p class="text-sm text-muted mt-4 mb-0">
              Секреты остаются только на backend. Здесь управляется маршрутизация SMS и видно состояние каналов.
            </p>
          </div>
          <button class="btn btn-sm btn-outline-primary" :disabled="refreshing" @click="refreshStatuses">
            {{ refreshing ? 'Проверяем…' : 'Обновить балансы' }}
          </button>
        </div>

        <div class="provider-status-grid mt-6">
          <div v-for="provider in providers" :key="`status-${provider.key}`" class="provider-status-card">
            <div class="provider-status-head">
              <b>{{ provider.label }}</b>
              <span :class="['provider-dot', providerHealth(provider.key)]"></span>
            </div>
            <div class="provider-balance">{{ providerBalance(provider.key) }}</div>
            <div class="provider-detail">{{ providerDetail(provider.key, provider.configured) }}</div>
          </div>
        </div>

        <div class="section-block mt-8">
          <div class="section-title">Маршрутизация обычных SMS</div>
          <div class="flex flex-col space-y-4 mt-5">
            <b class="text-sm">Основной провайдер</b>
            <div
              v-for="provider in providers"
              :key="`primary-${provider.key}`"
              class="flex items-center">
              <RadioButton
                :checked="local.primaryProvider === provider.key"
                :value="provider.key"
                :class="{ 'opacity-50': !provider.configured }"
                @change="selectPrimary(provider)">
                <span class="font-bold mr-3">{{ provider.label }}</span>
                <span :class="provider.configured ? 'text-primary' : 'text-danger'" class="text-xs">
                  {{ provider.configured ? 'настроен' : 'не настроен на сервере' }}
                </span>
              </RadioButton>
            </div>
          </div>

          <div class="flex flex-col space-y-4 mt-6">
            <Check
              label="Использовать резервного провайдера при безопасной для переключения ошибке"
              :value="local.fallbackEnabled"
              :disabled="!canEdit"
              @input="onFallbackEnabled"/>

            <div class="flex flex-col space-y-4 pl-8">
              <b class="text-sm">Резервный провайдер</b>
              <div class="text-xs text-muted">
                Выбор сохраняется даже при выключенном автопереходе. При необходимости его можно сделать основным одной настройкой.
              </div>
              <div v-for="provider in fallbackProviders" :key="`fallback-${provider.key}`">
                <RadioButton
                  :checked="local.fallbackProvider === provider.key"
                  :value="provider.key"
                  :class="{ 'opacity-50': !provider.configured }"
                  @change="selectFallback(provider)">
                  <span class="font-bold mr-3">{{ provider.label }}</span>
                  <span :class="provider.configured ? 'text-primary' : 'text-danger'" class="text-xs">
                    {{ provider.configured ? 'настроен' : 'не настроен на сервере' }}
                  </span>
                </RadioButton>
              </div>
            </div>
          </div>

          <div class="route-summary mt-6">
            <div class="text-sm">
              <b>Текущая схема:</b>
              {{ providerLabel(local.primaryProvider) }}
              <template v-if="local.fallbackProvider">
                · резерв: {{ providerLabel(local.fallbackProvider) }}
                <span v-if="local.fallbackEnabled">(автопереход включён)</span>
                <span v-else>(автопереход выключен)</span>
              </template>
            </div>
            <div class="text-xs text-muted mt-2">
              Резерв включается только при явном отказе основного провайдера до принятия сообщения в очередь.
              При неопределённом сетевом timeout второй SMS не отправляется, чтобы не создавать дубль.
            </div>
          </div>
        </div>

        <div class="section-block mt-8">
          <div class="section-title">Доступность ручных каналов</div>
          <div class="channel-status-list mt-4">
            <div v-for="item in channels" :key="item.key" class="channel-status-row">
              <div>
                <b>{{ item.label }}</b>
                <div class="text-xs text-muted mt-1">{{ item.detail }}</div>
              </div>
              <span :class="['channel-status-badge', item.enabled ? 'enabled' : 'disabled']">
                {{ item.enabled ? 'доступен' : 'отключён' }}
              </span>
            </div>
          </div>
        </div>

        <div class="mt-8" v-if="canEdit">
          <button class="btn btn-primary font-normal" :disabled="saving || !canSave" @click="save">
            {{ saving ? 'Сохранение…' : 'Сохранить маршрутизацию' }}
          </button>
        </div>
        <div v-else class="text-sm text-muted mt-8">Для изменения требуется право «Настройки».</div>
      </div>
    </Loading>
  </div>
</template>

<script>
import _ from 'lodash';
import Loading from '@/components/Loading.vue';
import Check from '@/components/Check.vue';
import RadioButton from '@/components/RadioButton.vue';
import { smsSettings, updateSmsSettings, smsProviderStatus } from '@/api/settings';
import { iwalyNotify } from '@/notify';

export default {
  components: { Loading, Check, RadioButton },
  data() {
    return {
      loading: true,
      saving: false,
      refreshing: false,
      providers: [],
      providerStatus: [],
      channels: [],
      canEdit: false,
      local: {
        primaryProvider: 'smsaero',
        fallbackProvider: 'smsimple',
        fallbackEnabled: false,
      },
    };
  },
  computed: {
    fallbackProviders() {
      return this.providers.filter((provider) => provider.key !== this.local.primaryProvider);
    },
    canSave() {
      const primary = this.providers.find((provider) => provider.key === this.local.primaryProvider);
      if (!primary || !primary.configured) return false;
      if (!this.local.fallbackEnabled) return true;
      const fallback = this.providers.find((provider) => provider.key === this.local.fallbackProvider);
      return !!fallback && fallback.configured && fallback.key !== primary.key;
    },
  },
  methods: {
    providerLabel(key) {
      return _.get(this.providers.find((provider) => provider.key === key), 'label', key || '—');
    },
    statusFor(key) {
      return this.providerStatus.find((item) => item.key === key) || null;
    },
    providerHealth(key) {
      const item = this.statusFor(key);
      if (!item) return 'unknown';
      if (item.status === 'ok') return 'ok';
      if (item.status === 'not_configured') return 'warning';
      return 'failed';
    },
    providerBalance(key) {
      const item = this.statusFor(key);
      if (!item || item.balance === null || item.balance === undefined) return 'Баланс —';
      return `${Number(item.balance).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
    },
    providerDetail(key, configured) {
      const item = this.statusFor(key);
      if (!configured) return 'Реквизиты не заполнены на backend';
      if (!item) return 'Статус ещё не проверен';
      if (item.status === 'ok') return 'API доступен';
      return item.error || 'API недоступен';
    },
    selectPrimary(provider) {
      if (!this.canEdit || !provider.configured) return;
      this.local.primaryProvider = provider.key;
      if (this.local.fallbackProvider === provider.key) {
        const next = this.fallbackProviders.find((item) => item.configured);
        this.local.fallbackProvider = next ? next.key : null;
      }
    },
    selectFallback(provider) {
      if (!this.canEdit || !provider.configured) return;
      this.local.fallbackProvider = provider.key;
    },
    onFallbackEnabled(value) {
      if (!this.canEdit) return;
      this.local.fallbackEnabled = value;
      if (value && !this.local.fallbackProvider) {
        const next = this.fallbackProviders.find((provider) => provider.configured);
        this.local.fallbackProvider = next ? next.key : null;
      }
    },
    applyData(data) {
      this.providers = data.providers || this.providers;
      this.providerStatus = data.providerStatus || this.providerStatus;
      this.channels = data.channels || this.channels;
      if (typeof data.canEdit !== 'undefined') this.canEdit = !!data.canEdit;
      if (data.primaryProvider) {
        this.local = {
          primaryProvider: data.primaryProvider || 'smsaero',
          fallbackProvider: data.fallbackProvider || null,
          fallbackEnabled: !!data.fallbackEnabled,
        };
      }
    },
    async fetch() {
      this.loading = true;
      try {
        const response = await smsSettings();
        this.applyData(response.data || {});
      } finally {
        this.loading = false;
      }
    },
    async refreshStatuses() {
      this.refreshing = true;
      try {
        const response = await smsProviderStatus(true);
        this.providerStatus = (response.data && response.data.providerStatus) || [];
        iwalyNotify.success({ text: 'Балансы SMS-провайдеров обновлены' });
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось обновить статусы SMS-провайдеров' });
      } finally {
        this.refreshing = false;
      }
    },
    async save() {
      if (!this.canEdit || !this.canSave) return;
      this.saving = true;
      try {
        const response = await updateSmsSettings(this.local);
        this.applyData(response.data || {});
        iwalyNotify.success({ text: 'Настройки SMS сохранены' });
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>

<style scoped>
.communication-settings { max-width: 1050px; }
.settings-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
.provider-status-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.provider-status-card { border: 1px solid #dce1e8; border-radius: 9px; background: #fff; padding: 16px; }
.provider-status-head { display: flex; justify-content: space-between; align-items: center; }
.provider-dot { width: 9px; height: 9px; border-radius: 50%; background: #aeb4bd; }
.provider-dot.ok { background: #32a267; }
.provider-dot.warning { background: #d79a32; }
.provider-dot.failed { background: #c95050; }
.provider-balance { font-size: 22px; line-height: 28px; font-weight: 700; margin-top: 10px; }
.provider-detail { font-size: 11px; color: #7c8591; margin-top: 5px; }
.section-block { border-top: 1px solid #e1e4e9; padding-top: 22px; }
.section-title { font-size: 15px; font-weight: 700; }
.route-summary { border: 1px solid #d9dee8; border-radius: 8px; padding: 15px; background: #f8f9fb; }
.channel-status-list { display: flex; flex-direction: column; gap: 9px; }
.channel-status-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid #e0e4ea;
  border-radius: 7px;
  background: #fff;
}
.channel-status-badge { border-radius: 12px; padding: 4px 9px; font-size: 10px; }
.channel-status-badge.enabled { background: #e9f7ef; color: #25884f; }
.channel-status-badge.disabled { background: #f4f1ea; color: #8a6a32; }
</style>
